import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import "./PrivacyPolicy.css";

const PrivacyPolicy = () => {
  const [privacyContent, setPrivacyContent] = useState("");
  const [editableContent, setEditableContent] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        setLoading(true);
        const docRef = doc(db, "privacyPolicy", "content");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setPrivacyContent(data.text || "");
          setEditableContent(data.text || "");
          setLastUpdated(data.lastUpdated?.toDate() || null);
        } else {
          console.log("No privacy policy document found!");
          setPrivacyContent("No privacy policy content available.");
          setEditableContent("No privacy policy content available.");
        }
      } catch (error) {
        console.error("Error fetching privacy policy:", error);
        alert("Failed to load privacy policy. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchPrivacyPolicy();
  }, []);

  const handleEdit = () => {
    if (isEditing) {
      // Cancel editing - reset to original content
      setEditableContent(privacyContent);
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    if (!editableContent.trim()) {
      alert("Privacy policy content cannot be empty!");
      return;
    }

    try {
      setSaving(true);
      const docRef = doc(db, "privacyPolicy", "content");
      const now = new Date();
      
      await updateDoc(docRef, {
        text: editableContent,
        lastUpdated: now,
      });
      
      setPrivacyContent(editableContent);
      setLastUpdated(now);
      setIsEditing(false);
      
      // Show success message
      setSuccessMessage("Privacy policy updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      
    } catch (error) {
      console.error("Error updating privacy policy:", error);
      alert("Failed to update privacy policy. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "Never";
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="privacy-policy-wrapper">
        <div className="loading-container">
          <div className="spinner"></div>
          <h3>Loading Privacy Policy...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="privacy-policy-wrapper">
      {/* Success Message */}
      {successMessage && (
        <div className="success-alert">
          <i className="fas fa-check-circle"></i>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Header Section */}
      <div className="privacy-header">
        <div className="header-content">
          <div className="header-left">
            <h1 className="privacy-title">
              <i className="fas fa-shield-alt"></i>
              Privacy Policy for YBe
            </h1>
            {lastUpdated && (
              <p className="last-updated">
                <i className="fas fa-clock"></i>
                Last updated: {formatDate(lastUpdated)}
              </p>
            )}
          </div>
          <div className="header-actions">
            {!isEditing ? (
              <button onClick={handleEdit} className="btn-edit">
                <i className="fas fa-edit"></i>
                Edit Policy
              </button>
            ) : (
              <>
                <button onClick={handleSave} className="btn-save" disabled={saving}>
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Save Changes
                    </>
                  )}
                </button>
                <button onClick={handleEdit} className="btn-cancel" disabled={saving}>
                  <i className="fas fa-times"></i>
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="privacy-content">
        {isEditing ? (
          <div className="editor-container">
            <div className="editor-toolbar">
              <div className="toolbar-info">
                <i className="fas fa-info-circle"></i>
                <span>You can use HTML tags for formatting (e.g., &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;strong&gt;)</span>
              </div>
              <div className="character-count">
                {editableContent.length} characters
              </div>
            </div>
            <textarea
              value={editableContent}
              onChange={(e) => setEditableContent(e.target.value)}
              className="privacy-editor"
              placeholder="Enter privacy policy content here..."
            />
          </div>
        ) : (
          <div className="privacy-display">
            <div 
              className="privacy-text" 
              dangerouslySetInnerHTML={{ __html: privacyContent }} 
            />
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="info-card">
        <div className="info-header">
          <i className="fas fa-lightbulb"></i>
          <h3>Important Information</h3>
        </div>
        <ul className="info-list">
          <li>
            <i className="fas fa-check"></i>
            Changes will be immediately visible to all users
          </li>
          <li>
            <i className="fas fa-check"></i>
            Make sure to review all changes before saving
          </li>
          <li>
            <i className="fas fa-check"></i>
            Keep the privacy policy clear and easy to understand
          </li>
          <li>
            <i className="fas fa-check"></i>
            Include all necessary legal information
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PrivacyPolicy;